import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import SoundManager from '../utils/SoundManager';
import { disposeScene } from '../utils/ThreeCleanup';

// Interfaces
interface Choice {
  id: string;
  text: string;
}

interface Question {
  id: string;
  text: string;
  choices: Choice[];
}

interface DungeonState {
  currentQuestionIndex: number;
  selectedChoices: string[];
  phase: 'intro' | 'question' | 'transition' | 'conclusion';
  transitionProgress: number;
}

// CYBERPUNK INTERROGATION theme
const Dungeon1Visualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const cleanupFnsRef = useRef<(() => void)[]>([]);

  const [dungeonState, setDungeonState] = useState<DungeonState>({
    currentQuestionIndex: -1,
    selectedChoices: [],
    phase: 'intro',
    transitionProgress: 0
  });

  const [displayText, setDisplayText] = useState<string>('');
  const [showChoices, setShowChoices] = useState<boolean>(false);
  const [conclusion, setConclusion] = useState<boolean>(false);

  const textAnimationRef = useRef<number | null>(null);
  const typingPositionRef = useRef<number>(0);
  const targetTextRef = useRef<string>('');

  const questions: Question[] = [
    {
      id: 'human',
      text: "What makes you human?",
      choices: [
        { id: 'dna', text: "DNA" },
        { id: 'memories', text: "Memories" },
        { id: 'nothing', text: "Nothing" }
      ]
    },
    {
      id: 'upload',
      text: "Would you upload your mind?",
      choices: [
        { id: 'yes', text: "Yes" },
        { id: 'copy', text: "Only a copy" },
        { id: 'done', text: "Already done" }
      ]
    },
    {
      id: 'narrative',
      text: "Who controls the narrative?",
      choices: [
        { id: 'corps', text: "Corporations" },
        { id: 'algos', text: "Algorithms" },
        { id: 'noone', text: "No one" }
      ]
    },
    {
      id: 'privacy',
      text: "Is privacy dead?",
      choices: [
        { id: 'never', text: "It was never alive" },
        { id: 'rich', text: "Only for the rich" },
        { id: 'chose', text: "I chose to share" }
      ]
    },
    {
      id: 'after',
      text: "What comes after humanity?",
      choices: [
        { id: 'ai', text: "AI" },
        { id: 'hybrid', text: "Hybrid" },
        { id: 'extinction', text: "Extinction" }
      ]
    }
  ];

  // Neon pink/cyan color scheme
  const NEON_PINK = 0xff0080;
  const NEON_CYAN = 0x00ffff;
  const NEON_PURPLE = 0xaa00ff;

  const animateText = (text: string, callback?: () => void) => {
    if (textAnimationRef.current !== null) {
      cancelAnimationFrame(textAnimationRef.current);
      textAnimationRef.current = null;
    }
    setDisplayText('');
    typingPositionRef.current = 0;
    targetTextRef.current = text;

    const typeNextChar = () => {
      if (typingPositionRef.current < targetTextRef.current.length) {
        const nextPosition = Math.min(typingPositionRef.current + 1, targetTextRef.current.length);
        const currentText = targetTextRef.current.substring(0, nextPosition);
        setDisplayText(currentText);
        if (typingPositionRef.current % 5 === 0) {
          SoundManager.playSound('keypress');
        }
        typingPositionRef.current = nextPosition;
        textAnimationRef.current = requestAnimationFrame(() => {
          setTimeout(typeNextChar, 60);
        });
      } else {
        textAnimationRef.current = null;
        if (callback) callback();
      }
    };
    textAnimationRef.current = requestAnimationFrame(typeNextChar);
  };

  const getChoiceColor = (index: number) => {
    const colors = [NEON_PINK, NEON_CYAN, NEON_PURPLE];
    return colors[index % colors.length];
  };

  const selectChoice = (questionIndex: number, choiceIndex: number) => {
    SoundManager.playSound('command');
    const selectedChoice = questions[questionIndex].choices[choiceIndex].id;
    setDungeonState(prev => ({
      ...prev,
      selectedChoices: [...prev.selectedChoices, selectedChoice],
      phase: 'transition'
    }));
    setShowChoices(false);
    if (sceneRef.current) {
      createTransitionScene(sceneRef.current);
    }
  };

  // Helper: flickering neon light
  const createNeonLight = (scene: THREE.Scene, color: number, position: THREE.Vector3, intensity: number) => {
    const light = new THREE.PointLight(color, intensity, 15);
    light.position.copy(position);
    scene.add(light);

    const flicker = () => {
      if (!light.parent) return;
      const time = Date.now() * 0.001;
      light.intensity = intensity * (0.6 + Math.random() * 0.4 + Math.sin(time * 8) * 0.2);
      requestAnimationFrame(flicker);
    };
    flicker();
    return light;
  };

  // Intro scene: Dark interrogation room
  const createIntroScene = (scene: THREE.Scene) => {
    // Floor
    const floorGeo = new THREE.PlaneGeometry(30, 30);
    const floorMat = new THREE.MeshPhongMaterial({ color: 0x0a0a0a, shininess: 80 });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -2;
    scene.add(floor);

    // Interrogation table
    const tableGeo = new THREE.BoxGeometry(4, 0.2, 2);
    const tableMat = new THREE.MeshPhongMaterial({ color: 0x222222, specular: 0x444444, shininess: 60 });
    const table = new THREE.Mesh(tableGeo, tableMat);
    table.position.y = -0.5;
    scene.add(table);

    // Table legs
    const legGeo = new THREE.CylinderGeometry(0.08, 0.08, 1.5, 8);
    const legMat = new THREE.MeshPhongMaterial({ color: 0x333333 });
    const legPositions = [[-1.8, -1.35, -0.8], [1.8, -1.35, -0.8], [-1.8, -1.35, 0.8], [1.8, -1.35, 0.8]];
    legPositions.forEach(pos => {
      const leg = new THREE.Mesh(legGeo, legMat);
      leg.position.set(pos[0], pos[1], pos[2]);
      scene.add(leg);
    });

    // Chair
    const chairSeatGeo = new THREE.BoxGeometry(1.2, 0.1, 1.2);
    const chairMat = new THREE.MeshPhongMaterial({ color: 0x1a1a1a });
    const chairSeat = new THREE.Mesh(chairSeatGeo, chairMat);
    chairSeat.position.set(0, -1, 3);
    scene.add(chairSeat);
    const chairBackGeo = new THREE.BoxGeometry(1.2, 1.5, 0.1);
    const chairBack = new THREE.Mesh(chairBackGeo, chairMat);
    chairBack.position.set(0, -0.3, 3.55);
    scene.add(chairBack);

    // Neon lights on walls
    createNeonLight(scene, NEON_PINK, new THREE.Vector3(-6, 3, -4), 2);
    createNeonLight(scene, NEON_CYAN, new THREE.Vector3(6, 3, -4), 2);
    createNeonLight(scene, NEON_PURPLE, new THREE.Vector3(0, 4, -5), 1.5);

    // Neon tube geometry (visual strips)
    const tubeGeo = new THREE.BoxGeometry(0.1, 0.1, 8);
    const tubePinkMat = new THREE.MeshBasicMaterial({ color: NEON_PINK, transparent: true, opacity: 0.9 });
    const tubeCyanMat = new THREE.MeshBasicMaterial({ color: NEON_CYAN, transparent: true, opacity: 0.9 });
    const tubeLeft = new THREE.Mesh(tubeGeo, tubePinkMat);
    tubeLeft.position.set(-6, 3, 0);
    scene.add(tubeLeft);
    const tubeRight = new THREE.Mesh(tubeGeo, tubeCyanMat);
    tubeRight.position.set(6, 3, 0);
    scene.add(tubeRight);

    // Flickering screens on back wall
    for (let i = 0; i < 3; i++) {
      const screenGeo = new THREE.PlaneGeometry(2, 1.5);
      const screenMat = new THREE.MeshBasicMaterial({
        color: i === 1 ? NEON_CYAN : NEON_PINK,
        transparent: true,
        opacity: 0.3
      });
      const screen = new THREE.Mesh(screenGeo, screenMat);
      screen.position.set(-3 + i * 3, 2, -5.9);
      scene.add(screen);

      const flickerScreen = () => {
        if (!screen.parent) return;
        if (screen.material instanceof THREE.MeshBasicMaterial) {
          screen.material.opacity = 0.15 + Math.random() * 0.35;
        }
        setTimeout(() => requestAnimationFrame(flickerScreen), 100 + Math.random() * 200);
      };
      flickerScreen();
    }

    // Back wall
    const wallGeo = new THREE.PlaneGeometry(30, 15);
    const wallMat = new THREE.MeshPhongMaterial({ color: 0x050510, side: THREE.DoubleSide });
    const wall = new THREE.Mesh(wallGeo, wallMat);
    wall.position.set(0, 3, -6);
    scene.add(wall);

    // Particle dust
    const dustCount = 600;
    const dustGeo = new THREE.BufferGeometry();
    const dustPos = new Float32Array(dustCount * 3);
    for (let i = 0; i < dustCount * 3; i += 3) {
      dustPos[i] = (Math.random() - 0.5) * 20;
      dustPos[i + 1] = Math.random() * 8 - 2;
      dustPos[i + 2] = (Math.random() - 0.5) * 15;
    }
    dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
    const dustMat = new THREE.PointsMaterial({
      color: NEON_CYAN,
      size: 0.03,
      transparent: true,
      opacity: 0.4,
      blending: THREE.AdditiveBlending
    });
    const dust = new THREE.Points(dustGeo, dustMat);
    scene.add(dust);

    const animateDust = () => {
      if (!dust.parent) return;
      const positions = dust.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < positions.length; i += 3) {
        positions[i] += (Math.random() - 0.5) * 0.01;
        positions[i + 1] += (Math.random() - 0.5) * 0.01;
      }
      dust.geometry.attributes.position.needsUpdate = true;
      requestAnimationFrame(animateDust);
    };
    animateDust();
  };

  // Question scene
  const createQuestionScene = (scene: THREE.Scene, questionIndex: number) => {
    while (scene.children.length > 2) {
      scene.remove(scene.children[0]);
    }

    // Dark floor with grid lines (cyberpunk style)
    const floorGeo = new THREE.PlaneGeometry(40, 40, 40, 40);
    const floorMat = new THREE.MeshBasicMaterial({
      color: 0x000011,
      wireframe: true,
      transparent: true,
      opacity: 0.3
    });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -3;
    scene.add(floor);

    // Solid floor underneath
    const solidFloorGeo = new THREE.PlaneGeometry(40, 40);
    const solidFloorMat = new THREE.MeshPhongMaterial({ color: 0x000005 });
    const solidFloor = new THREE.Mesh(solidFloorGeo, solidFloorMat);
    solidFloor.rotation.x = -Math.PI / 2;
    solidFloor.position.y = -3.01;
    scene.add(solidFloor);

    // Neon pillars
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const radius = 10;
      const pillarGeo = new THREE.BoxGeometry(0.3, 12, 0.3);
      const pillarMat = new THREE.MeshBasicMaterial({
        color: i % 2 === 0 ? NEON_PINK : NEON_CYAN,
        transparent: true,
        opacity: 0.6
      });
      const pillar = new THREE.Mesh(pillarGeo, pillarMat);
      pillar.position.set(Math.cos(angle) * radius, 3, Math.sin(angle) * radius);
      scene.add(pillar);

      createNeonLight(scene, i % 2 === 0 ? NEON_PINK : NEON_CYAN,
        new THREE.Vector3(Math.cos(angle) * radius, 6, Math.sin(angle) * radius), 0.8);
    }

    // Holographic choice panels
    const question = questions[questionIndex];
    question.choices.forEach((_choice, index) => {
      const angle = ((index / question.choices.length) * Math.PI * 2) + Math.PI / 6;
      const radius = 5;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;

      // Hexagonal panel
      const panelGeo = new THREE.CircleGeometry(1.2, 6);
      const panelMat = new THREE.MeshBasicMaterial({
        color: getChoiceColor(index),
        transparent: true,
        opacity: 0.7,
        side: THREE.DoubleSide
      });
      const panel = new THREE.Mesh(panelGeo, panelMat);
      panel.position.set(x, 0, z);
      panel.rotation.y = -angle + Math.PI / 2;
      (panel as any).choiceIndex = index;
      scene.add(panel);

      // Border ring
      const ringGeo = new THREE.RingGeometry(1.2, 1.35, 6);
      const ringMat = new THREE.MeshBasicMaterial({
        color: getChoiceColor(index),
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(x, 0, z);
      ring.rotation.y = -angle + Math.PI / 2;
      scene.add(ring);

      const pointLight = new THREE.PointLight(getChoiceColor(index), 1, 5);
      pointLight.position.set(x, 0, z);
      scene.add(pointLight);

      // Hover animation
      const animatePanel = () => {
        if (!panel.parent) return;
        const time = Date.now() * 0.001;
        panel.position.y = Math.sin(time * 1.5 + index) * 0.3;
        ring.position.y = panel.position.y;
        if (panel.material instanceof THREE.MeshBasicMaterial) {
          panel.material.opacity = 0.5 + Math.sin(time * 2 + index) * 0.2;
        }
        requestAnimationFrame(animatePanel);
      };
      animatePanel();
    });

    // Data particles
    const particlesCount = 800;
    const particlesGeo = new THREE.BufferGeometry();
    const particlePos = new Float32Array(particlesCount * 3);
    for (let i = 0; i < particlesCount * 3; i += 3) {
      particlePos[i] = (Math.random() - 0.5) * 30;
      particlePos[i + 1] = Math.random() * 15 - 3;
      particlePos[i + 2] = (Math.random() - 0.5) * 30;
    }
    particlesGeo.setAttribute('position', new THREE.BufferAttribute(particlePos, 3));
    const particleMat = new THREE.PointsMaterial({
      color: NEON_CYAN,
      size: 0.04,
      transparent: true,
      opacity: 0.5,
      blending: THREE.AdditiveBlending
    });
    const particles = new THREE.Points(particlesGeo, particleMat);
    scene.add(particles);

    // Raycaster
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const handleMouseMove = (event: MouseEvent) => {
      if (!containerRef.current || !cameraRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mouse.x = ((event.clientX - rect.left) / containerRef.current.clientWidth) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / containerRef.current.clientHeight) * 2 + 1;
      raycaster.setFromCamera(mouse, cameraRef.current);
      const intersects = raycaster.intersectObjects(scene.children);

      scene.children.forEach(child => {
        if (child instanceof THREE.Mesh && child.geometry instanceof THREE.CircleGeometry && 'choiceIndex' in child) {
          if (child.material instanceof THREE.MeshBasicMaterial) {
            child.scale.set(1, 1, 1);
          }
        }
      });

      if (intersects.length > 0) {
        const object = intersects[0].object;
        if (object instanceof THREE.Mesh && 'choiceIndex' in object) {
          object.scale.set(1.3, 1.3, 1.3);
        }
      }
    };

    const handleClick = () => {
      if (!cameraRef.current) return;
      raycaster.setFromCamera(mouse, cameraRef.current);
      const intersects = raycaster.intersectObjects(scene.children);
      if (intersects.length > 0) {
        const object = intersects[0].object;
        if (object instanceof THREE.Mesh && 'choiceIndex' in object) {
          selectChoice(questionIndex, (object as any).choiceIndex);
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);
    cleanupFnsRef.current.push(() => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
    });
  };

  // Transition: data stream tunnel
  const createTransitionScene = (scene: THREE.Scene) => {
    while (scene.children.length > 2) {
      scene.remove(scene.children[0]);
    }

    // Data stream tunnel with neon colors
    const tunnelPoints: THREE.Vector3[] = [];
    for (let i = 0; i < 25; i++) {
      tunnelPoints.push(new THREE.Vector3(
        Math.sin(i * 0.3) * 2,
        Math.cos(i * 0.3) * 0.5,
        -5 * i
      ));
    }
    const tunnelPath = new THREE.CatmullRomCurve3(tunnelPoints);
    const tunnelGeo = new THREE.TubeGeometry(tunnelPath, 80, 2.5, 12, false);
    const tunnelMat = new THREE.MeshBasicMaterial({
      color: NEON_PINK,
      wireframe: true,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide
    });
    const tunnel = new THREE.Mesh(tunnelGeo, tunnelMat);
    scene.add(tunnel);

    // Data stream particles
    const streamCount = 1500;
    const streamGeo = new THREE.BufferGeometry();
    const streamPos = new Float32Array(streamCount * 3);
    for (let i = 0; i < streamCount * 3; i += 3) {
      const t = Math.random();
      const pos = tunnelPath.getPoint(t);
      const theta = Math.random() * Math.PI * 2;
      const r = Math.random() * 2;
      streamPos[i] = pos.x + Math.cos(theta) * r;
      streamPos[i + 1] = pos.y + Math.sin(theta) * r;
      streamPos[i + 2] = pos.z;
    }
    streamGeo.setAttribute('position', new THREE.BufferAttribute(streamPos, 3));
    const streamMat = new THREE.PointsMaterial({
      color: NEON_CYAN,
      size: 0.06,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending
    });
    const stream = new THREE.Points(streamGeo, streamMat);
    scene.add(stream);

    // End light
    const endLight = new THREE.PointLight(NEON_PINK, 2, 100);
    endLight.position.set(0, 0, -120);
    scene.add(endLight);

    if (cameraRef.current) {
      cameraRef.current.position.set(0, 0, 5);
      cameraRef.current.lookAt(0, 0, -10);
    }

    let progress = 0;
    const animateTransition = () => {
      if (!cameraRef.current) return;
      progress += 0.006;
      setDungeonState(prev => ({ ...prev, transitionProgress: Math.min(1, progress) }));

      if (progress <= 1) {
        const pos = tunnelPath.getPoint(progress);
        cameraRef.current.position.copy(pos);
        const lookAt = tunnelPath.getPoint(Math.min(1, progress + 0.1));
        cameraRef.current.lookAt(lookAt);
        requestAnimationFrame(animateTransition);
      } else {
        if (dungeonState.currentQuestionIndex >= questions.length - 1) {
          startConclusion();
        } else {
          setDungeonState(prev => ({
            ...prev,
            currentQuestionIndex: prev.currentQuestionIndex + 1,
            phase: 'question',
            transitionProgress: 0
          }));
        }
      }
    };
    animateTransition();
  };

  const startConclusion = () => {
    setDungeonState(prev => ({ ...prev, phase: 'conclusion' }));
    if (sceneRef.current) {
      createConclusionScene(sceneRef.current);
    }
  };

  const createConclusionScene = (scene: THREE.Scene) => {
    while (scene.children.length > 2) {
      scene.remove(scene.children[0]);
    }

    // Dark platform
    const platformGeo = new THREE.CircleGeometry(12, 32);
    const platformMat = new THREE.MeshPhongMaterial({ color: 0x050510, shininess: 80 });
    const platform = new THREE.Mesh(platformGeo, platformMat);
    platform.rotation.x = -Math.PI / 2;
    platform.position.y = -2;
    scene.add(platform);

    // Central holographic eye / surveillance orb
    const orbGeo = new THREE.IcosahedronGeometry(3, 2);
    const orbMat = new THREE.MeshBasicMaterial({
      color: NEON_PINK,
      wireframe: true,
      transparent: true,
      opacity: 0.7
    });
    const orb = new THREE.Mesh(orbGeo, orbMat);
    orb.position.y = 3;
    scene.add(orb);

    // Inner orb
    const innerOrbGeo = new THREE.IcosahedronGeometry(1.5, 1);
    const innerOrbMat = new THREE.MeshBasicMaterial({
      color: NEON_CYAN,
      wireframe: true,
      transparent: true,
      opacity: 0.9
    });
    const innerOrb = new THREE.Mesh(innerOrbGeo, innerOrbMat);
    innerOrb.position.y = 3;
    scene.add(innerOrb);

    const orbLight = new THREE.PointLight(NEON_PINK, 2, 20);
    orbLight.position.set(0, 3, 0);
    scene.add(orbLight);

    const animateOrb = () => {
      if (!orb.parent) return;
      const time = Date.now() * 0.001;
      orb.rotation.y += 0.005;
      orb.rotation.x += 0.003;
      innerOrb.rotation.y -= 0.008;
      innerOrb.rotation.z += 0.004;
      orbLight.intensity = 1.5 + Math.sin(time * 3) * 0.5;
      orb.scale.setScalar(1 + Math.sin(time * 2) * 0.05);
      requestAnimationFrame(animateOrb);
    };
    animateOrb();

    // Surrounding data particles
    const pCount = 600;
    const pGeo = new THREE.BufferGeometry();
    const pPos = new Float32Array(pCount * 3);
    for (let i = 0; i < pCount * 3; i += 3) {
      const phi = Math.random() * Math.PI * 2;
      const theta = Math.random() * Math.PI;
      const r = 4 + Math.random() * 2;
      pPos[i] = r * Math.sin(theta) * Math.cos(phi);
      pPos[i + 1] = r * Math.sin(theta) * Math.sin(phi) + 3;
      pPos[i + 2] = r * Math.cos(theta);
    }
    pGeo.setAttribute('position', new THREE.BufferAttribute(pPos, 3));
    const pMat = new THREE.PointsMaterial({
      color: NEON_CYAN,
      size: 0.05,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });
    const particlesObj = new THREE.Points(pGeo, pMat);
    scene.add(particlesObj);

    if (cameraRef.current) {
      cameraRef.current.position.set(0, 5, 12);
      cameraRef.current.lookAt(0, 3, 0);
    }

    // Collapse animation then reveal text
    setTimeout(() => {
      const startTime = Date.now();
      const duration = 4000;
      const collapse = () => {
        if (!orb.parent) return;
        const elapsed = Date.now() - startTime;
        const p = Math.min(1, elapsed / duration);
        const s = 1 - p * 0.7;
        orb.scale.setScalar(s);
        innerOrb.scale.setScalar(s * 1.2);
        if (orbMat instanceof THREE.MeshBasicMaterial) orbMat.opacity = 0.7 * (1 - p);
        if (p < 1) {
          requestAnimationFrame(collapse);
        }
      };
      collapse();

      setTimeout(() => {
        setConclusion(true);
        animateText(
          "THE MACHINE DOESN'T CARE ABOUT YOUR ANSWERS. IT ONLY NEEDED YOUR PATTERNS.",
          () => { SoundManager.playSound('command'); }
        );
      }, duration + 1000);
    }, 3000);
  };

  const startSequence = () => {
    SoundManager.playSound('toggle');
    setTimeout(() => {
      setDungeonState(prev => ({
        ...prev,
        currentQuestionIndex: 0,
        phase: 'question'
      }));
      if (sceneRef.current) {
        createQuestionScene(sceneRef.current, 0);
      }
      animateText(questions[0].text, () => { setShowChoices(true); });
    }, 2000);
  };

  // Initialize
  useEffect(() => {
    if (!containerRef.current) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000005);
    scene.fog = new THREE.FogExp2(0x000005, 0.008);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(
      75, containerRef.current.clientWidth / containerRef.current.clientHeight, 0.1, 1000
    );
    camera.position.set(0, 2, 10);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const ambientLight = new THREE.AmbientLight(0x101020);
    scene.add(ambientLight);
    const dirLight = new THREE.DirectionalLight(0x222244, 0.5);
    dirLight.position.set(0, 10, 10);
    scene.add(dirLight);

    createIntroScene(scene);
    animateText("SUBJECT DETECTED. INITIATING INTERROGATION PROTOCOL...", () => {
      setTimeout(startSequence, 2000);
    });

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      if (rendererRef.current && sceneRef.current && cameraRef.current) {
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
    };
    animate();

    const handleResize = () => {
      if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      cameraRef.current.aspect = w / h;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cleanupFnsRef.current.forEach(fn => fn());
      cleanupFnsRef.current = [];
      if (animFrameRef.current !== null) cancelAnimationFrame(animFrameRef.current);
      if (textAnimationRef.current !== null) cancelAnimationFrame(textAnimationRef.current);
      if (containerRef.current && rendererRef.current) {
        if (containerRef.current.contains(rendererRef.current.domElement)) {
          containerRef.current.removeChild(rendererRef.current.domElement);
        }
      }
      if (sceneRef.current && rendererRef.current) {
        disposeScene(sceneRef.current, rendererRef.current);
      }
    };
  }, []);

  // Phase changes
  useEffect(() => {
    if (dungeonState.phase === 'question' && dungeonState.currentQuestionIndex >= 0) {
      if (sceneRef.current) {
        createQuestionScene(sceneRef.current, dungeonState.currentQuestionIndex);
      }
      const qi = dungeonState.currentQuestionIndex;
      if (qi < questions.length) {
        animateText(questions[qi].text, () => { setShowChoices(true); });
      }
    }
  }, [dungeonState.phase, dungeonState.currentQuestionIndex]);

  useEffect(() => {
    return () => {
      if (textAnimationRef.current !== null) cancelAnimationFrame(textAnimationRef.current);
    };
  }, []);

  return (
    <div className="w-full h-full flex flex-col">
      <div className="relative flex-grow" ref={containerRef}>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center p-4">
            <div className="text-2xl mb-4 font-bold" style={{ color: '#ff0080', textShadow: '0 0 10px #ff0080, 0 0 20px #00ffff' }}>
              {!conclusion && displayText}
            </div>

            {showChoices && dungeonState.currentQuestionIndex >= 0 && dungeonState.currentQuestionIndex < questions.length && (
              <div className="flex flex-col space-y-4 mt-8 pointer-events-auto">
                {questions[dungeonState.currentQuestionIndex].choices.map((choice, index) => (
                  <button
                    key={choice.id}
                    className="px-4 py-2 border-2 bg-black bg-opacity-80 hover:bg-opacity-100 text-white font-mono"
                    style={{
                      borderColor: index === 0 ? '#ff0080' : index === 1 ? '#00ffff' : '#aa00ff',
                      textShadow: `0 0 5px ${index === 0 ? '#ff0080' : index === 1 ? '#00ffff' : '#aa00ff'}`,
                      boxShadow: `0 0 8px ${index === 0 ? '#ff008044' : index === 1 ? '#00ffff44' : '#aa00ff44'}`
                    }}
                    onClick={() => selectChoice(dungeonState.currentQuestionIndex, index)}
                  >
                    {choice.text}
                  </button>
                ))}
              </div>
            )}

            {conclusion && (
              <div className="bg-black bg-opacity-90 p-6 max-w-xl mx-auto border border-[#ff0080]" style={{ boxShadow: '0 0 20px #ff008044' }}>
                <p className="text-[#00ffff] whitespace-pre-line font-mono" style={{ textShadow: '0 0 5px #00ffff' }}>
                  {displayText}
                </p>
              </div>
            )}
          </div>
        </div>

        {dungeonState.currentQuestionIndex >= 0 && !conclusion && (
          <div className="absolute bottom-4 left-4 bg-black bg-opacity-80 p-3 rounded border border-[#ff0080]">
            <p className="text-sm font-mono" style={{ color: '#00ffff' }}>
              <span className="font-bold">QUERY {dungeonState.currentQuestionIndex + 1} OF 5</span>
              {dungeonState.phase === 'transition' && (
                <span className="ml-2">
                  TRANSMITTING... {Math.round(dungeonState.transitionProgress * 100)}%
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dungeon1Visualization;
